import type { APIDesignerStore } from '..';
import type { WritableDraft } from 'immer/dist/internal.js';
import type { OpenAPIV3 } from 'openapi-types';

import type { PartialSchema } from '@ui/APIDesigner/TypeMenu';

import { convertToType } from '../util';

const schemaPropertyMappingPattern = /0(-\d+)+/g;

type EnumValue = NonNullable<OpenAPIV3.SchemaObject['enum']>[number];
type EnumAction = { action: 'clear' } | { action: 'push'; value: EnumValue } | { action: 'remove'; value: number };

export type UpdateSchemaAttrObject =
  | { attribute: 'default'; value: NonNullable<OpenAPIV3.SchemaObject['default']> }
  | { attribute: 'description'; value: NonNullable<OpenAPIV3.SchemaObject['description']> }
  | { attribute: 'enum'; value: EnumAction }
  | { attribute: 'example'; value: string }
  | { attribute: 'name'; value: string }
  | { attribute: 'required'; value: boolean }
  | { attribute: 'type'; value: PartialSchema };

type TraverseAction =
  | {
      action: 'add' | 'remove';
    }
  | (UpdateSchemaAttrObject & { action: 'update' });

// Used to determine which param object in the parameters array the target is
interface ParamInfo {
  index: number;
  location: Exclude<OpenAPIV3.ParameterObject['in'], 'cookie'>;
}

export type SchemaEditorTarget = 'request' | 'response' | (ParamInfo & { target: 'query' });

export interface ValidResponseSchema {
  description: string;
  schemas: {
    type: MIMETypeValue;
    schema: OpenAPIV3.SchemaObject;
  }[];
  statusCode: string;
}

/**
 * MIME types that are supported in API Designer.
 *
 * This is used to validate the media type of a [media type object] (https://spec.openapis.org/oas/v3.1.0.html#media-type-object).
 * By default, the schemas for all MIME types are the same so we should in theory be able to edit all of them.
 * However, we currently only support editing one MIME type at a time.
 */
export const MIMEType = {
  // Structured data formats
  ApplicationCSV: 'application/csv',
  ApplicationJSON: 'application/json',
  ApplicationOctetStream: 'application/octet-stream',
  ApplicationFormURLEncoded: 'application/x-www-form-urlencoded',
  ApplicationXML: 'application/xml',

  // Multipart/form
  MultipartFormData: 'multipart/form-data',

  // Text formats
  TextCSV: 'text/csv',
  TextHTML: 'text/html',
  TextPlain: 'text/plain',
  TextXML: 'text/xml',
} as const;

export type MIMETypeValue = (typeof MIMEType)[keyof typeof MIMEType];

export const isTextBasedMIMEType = (mimeType: MIMETypeValue): boolean => {
  return (
    mimeType === MIMEType.TextCSV ||
    mimeType === MIMEType.TextHTML ||
    mimeType === MIMEType.TextPlain ||
    mimeType === MIMEType.TextXML
  );
};

export const isStreamBasedMIMEType = (mimeType: MIMETypeValue): boolean => {
  return mimeType === MIMEType.ApplicationOctetStream;
};

export const isStatusCode = (value: string): boolean => /^\d{3}$/.test(value);
export const isPropertyPath = (value: string): boolean => /^\d+(-\d+)*$/.test(value);

/**
 * Generates a unique property name that doesn't conflict with existing properties.
 * Uses the pattern 'property1', 'property2', etc.
 */
export const generateUniquePropertyName = (existingProperties: Record<string, unknown>): string => {
  let counter = 1;
  let propertyName = `property${counter}`;

  while (existingProperties[propertyName]) {
    counter += 1;
    propertyName = `property${counter}`;
  }

  return propertyName;
};

/**
 * Helper function to traverse the schema object and modify it based on the provided `propertyPath` and `action`.
 * This function works with request bodies, response bodies, and query parameters.
 *
 * @param propertyPath The path to the property to modify. This is a string that represents the path to the property in the schema.
 * The first element is always 0, which represents the top-level object.
 * If the propertyPath is "0", it means the entire request body should be removed (for request targets).
 * If the propertyPath is "0-1-2", it means the property should be removed from the third property of the second property of the root schema object.
 * @param schema The schema object to modify.
 * @param action The action to perform on the schema. This can be one of the following:
 * - `add`: Adds a new property to the schema.
 * - `remove`: Removes a property from the schema.
 * - `update`: Updates an attribute of the schema to a provided value.
 */
function traverseAndModifySchema(
  action: TraverseAction,
  propertyPath: string,
  schema: WritableDraft<OpenAPIV3.SchemaObject>,
) {
  if (!propertyPath.match(schemaPropertyMappingPattern)) {
    return;
  }

  // if the id is not 0, this means the id contains a map to a sub-property
  const propertyIndexMap = propertyPath.split('-').map(Number);
  // we strip away the first element since it's always 0, which represents the top-level object.
  propertyIndexMap.splice(0, 1);

  /**
   * Keeps track of the current nested level of the object being traversed.
   */
  let currentNestedLevel = 0;
  /**
   * Represents the total number of nested levels that were defined in `propertyIndexMap`.
   */
  const totalNestedLevels = propertyIndexMap.length;
  /**
   * The object to traverse. This is the current schema object schema.
   *
   * As we traverse the object and its sub-properties, we update this variable to point to the current object.
   */
  let objectToTraverse: WritableDraft<OpenAPIV3.SchemaObject> = schema || {};

  /**
   * This is needed for query params because the starting position might be in an array of objects, and the properties are under 'items'
   * whereas in a request body, the root is always an object
   */
  if (objectToTraverse.type === 'array' && objectToTraverse.items && 'properties' in objectToTraverse.items) {
    objectToTraverse = objectToTraverse.items;
  }

  /**
   * Traverse the object to find the property to add.
   * `propertyIndexMap` is an array of indices that represent the path to the property.
   *
   * For example, if the propertyIndexMap is `[1, 2]`, it means the the object to add a property to is
   * the 3rd property of the 2nd property of the root schema object.
   */
  propertyIndexMap.some((propertyIndex, i) => {
    const currentPropertyName = Object.keys(objectToTraverse.properties || {})[propertyIndex];

    if (typeof currentPropertyName !== 'string') {
      // handling in case the property index map contains invalid data
      return false;
    }

    const schemaToUpdate = objectToTraverse.properties?.[currentPropertyName];
    // This if-statement checks that we've reached the last nested level defined in `propertyIndexMap`
    // and that the current nested level is the same as the one we're on.
    // If so, we've found the object to make changes to to.
    if (i >= totalNestedLevels - 1 && i === currentNestedLevel) {
      switch (action.action) {
        // we're adding a new property to the schema
        case 'add':
          if (schemaToUpdate && 'properties' in schemaToUpdate) {
            const existingProperties = schemaToUpdate.properties || {};
            const newPropertyName = generateUniquePropertyName(existingProperties);
            schemaToUpdate.properties = {
              ...existingProperties,
              [newPropertyName]: { type: 'string' },
            };
          } else if (schemaToUpdate && 'items' in schemaToUpdate && 'properties' in schemaToUpdate.items) {
            // if the schemaToUpdate is an array of objects, we need to add the new property to the items object
            const existingProperties = schemaToUpdate.items.properties || {};
            const newPropertyName = generateUniquePropertyName(existingProperties);
            schemaToUpdate.items.properties = {
              ...existingProperties,
              [newPropertyName]: { type: 'string' },
            };
          }
          break;
        // we're removing a property from the schema
        case 'remove':
          if (objectToTraverse.properties) {
            delete objectToTraverse.properties[currentPropertyName];
          }

          // In addition to removing the property, we also need to remove it from the required array
          // (if it's in there).
          if (objectToTraverse.required) {
            const requiredIndex = objectToTraverse.required.indexOf(currentPropertyName);
            if (requiredIndex !== -1) {
              objectToTraverse.required.splice(requiredIndex, 1);
            }
          }
          break;
        // we're updating an attribute of the schema
        case 'update':
          // eslint-disable-next-line no-case-declarations
          const { attribute, value } = action;
          // we're updating a attribute of the schema
          if (
            // these two conditions are to ensure that we don't try to update a reference object
            // (which is unlikely, but this is a nice type guard)
            schemaToUpdate &&
            !('$ref' in schemaToUpdate)
          ) {
            switch (attribute) {
              case 'default':
                schemaToUpdate.default = value;
                break;
              case 'description':
                schemaToUpdate.description = value;
                break;
              case 'enum':
                switch (value.action) {
                  case 'push':
                    if (!schemaToUpdate.enum) {
                      schemaToUpdate.enum = [];
                    }
                    if (!schemaToUpdate.enum.includes(value.value)) {
                      schemaToUpdate.enum.push(value.value);
                    }
                    break;
                  case 'remove':
                    if (!schemaToUpdate.enum || value.value < 0 || value.value >= schemaToUpdate.enum.length) {
                      throw new Error(`Enum at index ${value.value} does not exist`);
                    }
                    schemaToUpdate.enum.splice(value.value, 1);
                    if (schemaToUpdate.enum.length === 0) {
                      delete schemaToUpdate.enum;
                    }
                    break;
                  case 'clear':
                    delete schemaToUpdate.enum;
                    break;
                  default:
                    throw new Error('Invalid enum action');
                }
                break;
              case 'name':
                if (objectToTraverse.properties) {
                  // This is a bit cumbersome, but rather than adding a new property to the object
                  // and deleting the old one, we recreate the entire object one key at a time,
                  // replacing the old property name with the new one. This is to ensure that
                  // the order of the properties is preserved.
                  objectToTraverse.properties = Object.keys(objectToTraverse.properties).reduce<
                    typeof objectToTraverse.properties
                  >((prev, curr) => {
                    if (curr === currentPropertyName) {
                      prev[value] = objectToTraverse.properties![curr];
                    } else {
                      prev[curr] = objectToTraverse.properties![curr];
                    }
                    return prev;
                  }, {});

                  // if the surrounding object has an array of required fields, we need to
                  // make sure it reflects the new property name
                  if (objectToTraverse.required) {
                    // check if the current property name is in the required array
                    // and if it is, we need to update it to the new property name
                    const requiredIndex = objectToTraverse.required.indexOf(currentPropertyName);
                    if (requiredIndex !== -1) {
                      objectToTraverse.required[requiredIndex] = value;
                    }

                    // remove any duplicates of the old and new property names, if they exist
                    objectToTraverse.required = objectToTraverse.required.filter(
                      (item, index) => index === requiredIndex || item !== currentPropertyName || item !== value,
                    );
                  }
                }
                break;
              case 'required':
                // if the property is required, we need to update the required array.
                // first, we check if the required array exists
                if (objectToTraverse.required) {
                  // we need to remove the property from the required array if it exists
                  if (!value) {
                    objectToTraverse.required = objectToTraverse.required.filter(prop => prop !== currentPropertyName);
                    if (!objectToTraverse.required.length) {
                      // if the required array is empty, we should delete it
                      delete objectToTraverse.required;
                    }
                  } else if (value) {
                    const requiredIndex = objectToTraverse.required.indexOf(currentPropertyName);
                    // the required array doesn't contain the property name and we need to add it
                    if (requiredIndex === -1) {
                      objectToTraverse.required.push(currentPropertyName);
                    }
                  }
                } else if (value) {
                  // if the required array doesn't exist, we need to create it
                  objectToTraverse.required = [currentPropertyName];
                }
                break;
              case 'type':
                // eslint-disable-next-line no-case-declarations
                const previousType = schemaToUpdate.type;
                schemaToUpdate.type = value.type;

                // set a format if it exists, otherwise delete it
                if (value.format) {
                  schemaToUpdate.format = value.format;
                } else {
                  delete schemaToUpdate.format;
                }

                // if the type is an object and there are no properties, create an empty properties object
                // otherwise, remove the `properties` and `required` fields
                if (value.type === 'object') {
                  if (!schemaToUpdate.properties) schemaToUpdate.properties = {};
                } else {
                  delete schemaToUpdate.properties;
                  delete schemaToUpdate.required;
                }

                // if the type is an array, set the items field
                // otherwise, remove the items field
                if (value.type === 'array' && value.items) {
                  (schemaToUpdate as OpenAPIV3.ArraySchemaObject).items = value.items;
                } else {
                  // @ts-expect-error we want to delete this property whether or not it exists
                  delete schemaToUpdate.items;
                }

                // try to ensure the enum values are of the correct type when in View mode
                if (previousType !== action.value.type && schemaToUpdate.enum && schemaToUpdate.enum.length > 0) {
                  schemaToUpdate.enum = schemaToUpdate.enum.map(enumValue =>
                    convertToType(enumValue, action.value.type),
                  );
                }
                break;
              default:
            }
          }
          break;
        default:
      }

      // We've successfully updated the schema, so we can return true
      // to stop traversing `propertyIndexMap`.
      return true;
    }

    // If we've reached this point in the logic, it means we haven't made any changes to the schema nor have we
    // reached the last nested level defined in `propertyIndexMap`.
    // If the current property is an object, we need to update `objectToTraverse` to point to the current property
    // so we do the traversal again.
    if (schemaToUpdate && 'properties' in schemaToUpdate && schemaToUpdate.properties) {
      currentNestedLevel += 1;
      objectToTraverse = schemaToUpdate;
    } else if (
      schemaToUpdate &&
      'items' in schemaToUpdate &&
      'type' in schemaToUpdate.items &&
      schemaToUpdate.items.type === 'object'
    ) {
      // if the schemaToUpdate is an array of objects, we need to recurse into the items object
      currentNestedLevel += 1;
      objectToTraverse = schemaToUpdate.items;
    }

    return false;
  });
}

/**
 * Traverses the request schema object and modifies it based on the provided `id`, `state`, `action`, and `mimeType`.
 * Mime type determines which schema object to modify.
 */
export function traverseAndModifyRequestSchema(
  id: string,
  state: WritableDraft<APIDesignerStore>,
  action: TraverseAction,
  mimeType?: MIMETypeValue,
): void {
  if (!state.apiObject) throw new Error('apiObject is required');
  if (!state.apiObject.schema) throw new Error('schema is required');
  if (!state.apiObject.schema.paths) throw new Error('paths is required');
  if (!state.apiObject.path) throw new Error('path is required');
  if (!state.apiObject.method) throw new Error('method is required');

  const currentPath = state.apiObject.path;
  const currentMethod = state.apiObject.method;

  if (!state.apiObject.schema.paths[currentPath]?.[currentMethod]) {
    throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
  }

  const requestBody = state.apiObject.schema.paths[currentPath][currentMethod]?.requestBody;

  if (!requestBody) {
    // no request body found, so we can't do anything
    return;
  }

  if ('$ref' in requestBody) {
    throw new Error('Cannot add a sub-property to a reference request body');
  }

  if (!mimeType) {
    throw new Error('mimeType is required');
  }

  // by the time we get here, we've done enough type checking to know
  // that currentSchema is a SchemaObject, so we can safely cast it
  const currentSchema = requestBody.content?.[mimeType]?.schema;

  if (!currentSchema) {
    // no JSON request body schema found, so we can't do anything
    return;
  }

  if ('$ref' in currentSchema) {
    throw new Error('Cannot add a sub-property to a reference request body');
  }

  traverseAndModifySchema(action, id, currentSchema);
}

/**
 * Traverses the response schema object and modifies it based on the provided `statusCode`, `id` and `action`.
 * Similar to traverseAndModifyRequestSchema but works specifically with response schemas.
 */
export function traverseAndModifyResponseSchema(
  statusCode: string,
  mimeType: MIMETypeValue,
  id: string,
  state: WritableDraft<APIDesignerStore>,
  action: TraverseAction,
): void {
  if (!state.apiObject) throw new Error('apiObject is required');
  if (!state.apiObject.schema) throw new Error('schema is required');
  if (!state.apiObject.schema.paths) throw new Error('paths is required');
  if (!state.apiObject.path) throw new Error('path is required');
  if (!state.apiObject.method) throw new Error('method is required');

  const currentPath = state.apiObject.path;
  const currentMethod = state.apiObject.method;

  if (!state.apiObject.schema.paths[currentPath]?.[currentMethod]) {
    throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
  }

  const response = state.apiObject.schema.paths[currentPath][currentMethod]?.responses?.[statusCode];

  if (!response) {
    // no response found, so we can't do anything
    return;
  }

  if ('$ref' in response) {
    throw new Error('Cannot add a sub-property to a reference response');
  }

  // by the time we get here, we've done enough type checking to know
  // that currentSchema is a SchemaObject, so we can safely cast it
  const currentSchema = response.content?.[mimeType]?.schema;

  if (!currentSchema) {
    // no JSON response schema found, so we can't do anything
    return;
  }

  if ('$ref' in currentSchema) {
    throw new Error('Cannot add a sub-property to a reference response');
  }

  traverseAndModifySchema(action, id, currentSchema);
}

/**
 * Traverses the query parameter schema object and modifies it
 * Gets the target parameter object from the parameters array by `paramIndex` and `location`.
 */
export function traverseAndModifyQueryParam(
  location: Exclude<OpenAPIV3.ParameterObject['in'], 'cookie'>,
  paramIndex: number,
  propertyPath: string,
  state: WritableDraft<APIDesignerStore>,
  action: TraverseAction,
): void {
  if (!state.apiObject) throw new Error('apiObject is required');
  if (!state.apiObject.schema) throw new Error('schema is required');
  if (!state.apiObject.schema.paths) throw new Error('paths is required');
  if (!state.apiObject.path) throw new Error('path is required');
  if (!state.apiObject.method) throw new Error('method is required');

  const currentPath = state.apiObject.path;
  const currentMethod = state.apiObject.method;

  if (!state.apiObject.schema.paths[currentPath]?.[currentMethod]) {
    throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
  }

  const parameters = (state.apiObject.schema.paths[currentPath][currentMethod].parameters || []).filter(
    param => 'in' in param && param.in === location,
  );

  const currentParameter = parameters[paramIndex];

  if (!currentParameter || !('schema' in currentParameter) || !currentParameter.schema) {
    throw new Error(`Parameter at index ${paramIndex} does not have a schema`);
  }

  if ('$ref' in currentParameter.schema) {
    throw new Error(`Cannot update parameter at index ${paramIndex} because it is a reference`);
  }

  const schema = currentParameter.schema;

  traverseAndModifySchema(action, propertyPath, schema);
}

/**
 * Traverses the text plain response schema object and modifies it based on the provided `statusCode`, `id` and `action`.
 * Similar to traverseAndModifyResponseSchema but works with text based response schemas instead of JSON response schemas.
 */
export function traverseAndModifyTextPlainResponseSchema(
  statusCode: string,
  id: string,
  state: WritableDraft<APIDesignerStore>,
  value: string,
  mimeType: MIMETypeValue,
): void {
  if (!state.apiObject) throw new Error('apiObject is required');
  if (!state.apiObject.schema) throw new Error('schema is required');
  if (!state.apiObject.schema.paths) throw new Error('paths is required');
  if (!state.apiObject.path) throw new Error('path is required');
  if (!state.apiObject.method) throw new Error('method is required');

  const currentPath = state.apiObject.path;
  const currentMethod = state.apiObject.method;

  if (!state.apiObject.schema.paths[currentPath]?.[currentMethod]) {
    throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
  }

  const response = state.apiObject.schema.paths[currentPath][currentMethod]?.responses?.[
    statusCode
  ] as OpenAPIV3.ResponseObject;
  if (!response) {
    throw new Error(`missing response for status code ${statusCode}`);
  }

  if ('$ref' in response) {
    throw new Error('Cannot modify a reference response');
  }

  const currentTextPlainResponse = response.content?.[mimeType];
  if (!currentTextPlainResponse) {
    throw new Error(`missing ${mimeType} response for status code ${statusCode}`);
  }

  const currentTextPlainResponseObject = (
    state.apiObject.schema.paths[currentPath][currentMethod]!.responses![statusCode]! as OpenAPIV3.ResponseObject
  ).content![mimeType] as OpenAPIV3.MediaTypeObject | undefined;

  if (!currentTextPlainResponseObject || !currentTextPlainResponseObject.schema) {
    throw new Error(`missing schema for ${mimeType} response for status code ${statusCode}`);
  }

  if ('$ref' in currentTextPlainResponseObject.schema) {
    throw new Error('Cannot modify a reference response');
  }

  (currentTextPlainResponseObject.schema as OpenAPIV3.SchemaObject).example = value;
}
